import { Injectable } from '@angular/core';
import { HttpHandler, HttpProgressEvent, HttpInterceptor, HttpSentEvent, HttpHeaderResponse, HttpUserEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, shareReplay } from 'rxjs/operators';
export const CACHE_KEY = 'X-No-Cache';

@Injectable()
export class NoCacheInterceptor implements HttpInterceptor {
  private cache = new Map<string, { response: HttpResponse<any>; timestamp: number }>();
  private readonly CACHE_DURATION = 2 * 60 * 1000; // 2 minutes for static content
  
  intercept(req: HttpRequest<any>, next: HttpHandler):
    Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> {
    
    // Handle explicit no-cache requests
    const noCache = req.headers.get(CACHE_KEY);
    if (noCache) {
      const nextReq = req.clone({
        headers: req.headers.delete(CACHE_KEY)
      }).clone({
        setHeaders: {
          'Cache-Control': 'no-store, max-age=1, stale-while-revalidate=59',
          'Expires': '0',
          'If-Modified-Since': '0'
        }
      });
      return next.handle(nextReq);
    }

    // Only cache GET requests
    if (req.method !== 'GET') {
      return next.handle(req);
    }

    // Check if this should be cached (static content only)
    if (!this.shouldCache(req.url)) {
      // If the request already has explicit no-cache headers (e.g. user API),
      // respect them instead of overriding with max-age
      const existingCacheControl = req.headers.get('Cache-Control') || '';
      if (existingCacheControl.includes('no-cache') || existingCacheControl.includes('no-store')) {
        return next.handle(req);
      }
      // Allow browser caching with short duration for other dynamic content
      const nextReq = req.clone({
        setHeaders: {
          'Cache-Control': 'max-age=60' // 1 minute for dynamic content
        }
      });
      return next.handle(nextReq);
    }

    // Handle caching for static content
    const cacheKey = this.getCacheKey(req);
    const cached = this.cache.get(cacheKey);

    // Return cached response if available and fresh
    if (cached && this.isCacheFresh(cached.timestamp)) {
      return of(cached.response.clone());
    }

    // Cache the response for static content
    return next.handle(req).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          this.cache.set(cacheKey, {
            response: event.clone(),
            timestamp: Date.now()
          });
        }
      }),
      shareReplay(1)
    );
  }

  private shouldCache(url: string): boolean {
    // Only cache truly static content that rarely changes
    const cacheablePatterns = [
      '/api/node/specialty', // Specialty data rarely changes
      '/api/node/institution', // Institution data is relatively static
      '/api/landing-pages', // Landing pages content
      '/assets/', // Static assets
      '/media/', // Media files
      '/translations/' // Translation files
    ];

    // Explicitly exclude anything user-specific or dynamic
    const excludePatterns = [
      '/api/user/',
      '/dashboard',
      '/search',
      '/compare',
      '/saved_search',
      '/field_udp_dashboard_array',
      '/field_user_notes',
      '/aci_user_data',
      '/webform_rest/',
      '/node/users_dashboard_programs'
    ];

    // Must match cacheable pattern and NOT match exclude patterns
    const isCacheable = cacheablePatterns.some(pattern => url.includes(pattern));
    const isExcluded = excludePatterns.some(pattern => url.includes(pattern));
    
    return isCacheable && !isExcluded;
  }

  private getCacheKey(req: HttpRequest<any>): string {
    return req.urlWithParams;
  }

  private isCacheFresh(timestamp: number): boolean {
    return Date.now() - timestamp < this.CACHE_DURATION;
  }

  // Public methods for cache management
  public clearCache(): void {
    this.cache.clear();
  }

  public clearCacheForUrl(url: string): void {
    for (const key of this.cache.keys()) {
      if (key.includes(url)) {
        this.cache.delete(key);
      }
    }
  }
}